import React, { useState, useEffect, memo } from "react";
import { employeeService, authService } from "../services/api";
import { getAuthItem } from "../utils/authStorage";
import EmployeeProfileDetails from "../components/Admin/create-employee/EmployeeProfileDetails";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { DsSpinner } from "../design-system";

const EmployeeProfile = memo(function EmployeeProfile() {
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const navigate = useNavigate();
  const EMPTY_EMPLOYEE = {
    empCode: "", name: "", gender: "", dateOfBirth: "", branch: "",
    designation: "", department: "", subDepartment: "", grade: "", category: "",
    reportingManager: "", employeeType: "", employmentStatus: "", dateOfJoin: "",
    mobileNumber: "", personalEmail: "", officialEmail: "", bloodGroup: "",
    aadhaarNo: "", pan: "", address: "",
    emergencyContact: { name: "", number: "", relationship: "" },
    familyMembers: [], bankName: "", bankAccountNo: "", ifscCode: "",
    qualifications: [], experiences: [],
  };

  const fetchEmployeeData = async () => {
    try {
      setLoading(true);

      // Fast path: use stored employeeId to skip the getCurrentUser round-trip
      const storedEmpId = getAuthItem("employeeId");
      const storedUserId = getAuthItem("userId");
      const directId = storedEmpId || storedUserId;

      if (directId) {
        try {
          const res = await employeeService.getEmployee(directId);
          if (res?.data) { setEmployee(res.data); return; }
        } catch (_) { /* fall through to getCurrentUser */ }
      }

      // Fallback: resolve via getCurrentUser
      try {
        const cur = await authService.getCurrentUser();
        const ud = cur?.data;
        if (ud) {
          const eid = ud.employeeId || ud.id;
          if (eid) {
            const res = await employeeService.getEmployee(eid);
            if (res?.data) { setEmployee(res.data); return; }
          } else {
            setEmployee(ud); return;
          }
        }
      } catch (_) { /* fall through */ }

      toast.error("Unable to load employee data. Please try refreshing.", { id: "load-error" });
      setEmployee(EMPTY_EMPLOYEE);
    } catch (error) {
      console.error("Failed to fetch employee data:", error);
      toast.error("An unexpected error occurred while loading employee data", { id: "unexpected-error" });
      setEmployee(EMPTY_EMPLOYEE);
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh employee data
  const refreshEmployeeData = async () => {
    toast.loading("Refreshing employee data...", { id: "refresh-loading" });
    await fetchEmployeeData();
  };

  useEffect(() => { fetchEmployeeData(); }, []);

  const handleCancel = () => {
    // Reset form data to original employee data
    // setEmployee(employee);
    navigate("/employee/home");
  };

  if (loading) {
    return (
      <div className="ds-page" role="status" aria-label="Loading employee profile">
        <div className="ds-page-shell">
          <div className="ds-page-content flex min-h-0 flex-1 items-center justify-center">
            <div className="ds-page-panel flex w-full max-w-md items-center justify-center p-10">
              <DsSpinner className="h-9 w-9" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ds-page" role="main" aria-label="Employee profile">
      <div className="ds-page-shell">
        <div className="ds-page-content-flush min-h-0 flex-1 overflow-hidden">
          <EmployeeProfileDetails
            employee={employee}
            darkMode={darkMode}
            onCancel={handleCancel}
            isEmployeeView={true}
          />
        </div>
      </div>
    </div>
  );
});

export default EmployeeProfile;
